import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState, AppDispatch } from '../index'
import { buildApiUrl, getApiBaseUrlCandidates } from '../../config/api'
import { setCredentials, logout } from '../slices/authSlice'

function buildHeaders(headers: Headers, api: { getState: () => unknown }) {
  const state = api.getState() as RootState
  const token = state.auth?.token

  if (import.meta.env.DEV) {
    console.log('[API] Token status:', token ? 'Present' : 'Missing', token ? `(${token.substring(0, 20)}...)` : '')
  }

  if (token) {
    headers.set('authorization', `Bearer ${token}`)
  } else {
    const localToken = localStorage.getItem('token')
    if (localToken) {
      headers.set('authorization', `Bearer ${localToken}`)
      if (import.meta.env.DEV) {
        console.log('[API] Using token from localStorage')
      }
    }
  }
  return headers
}

function createBaseQuery(baseUrl: string) {
  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, api) => buildHeaders(headers, api),
  })
}

const baseUrls = getApiBaseUrlCandidates()
const baseQueries = baseUrls.map((baseUrl) => ({
  baseUrl,
  query: createBaseQuery(baseUrl),
}))

function isNetworkFallbackError(error: { status?: number | string } | undefined): boolean {
  return error?.status === 'FETCH_ERROR' || error?.status === 'TIMEOUT_ERROR'
}

async function refreshToken(baseUrl: string, token: string) {
  const response = await fetch(buildApiUrl(baseUrl, '/auth/refresh'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    return null
  }
  return response.json()
}

// Base query with fallback across API hosts and automatic token refresh on 401
const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result: any = null
  let usedBaseUrl = baseQueries[0]?.baseUrl || baseUrls[0]

  for (const { baseUrl, query } of baseQueries) {
    const candidateResult = await query(args, api, extraOptions)

    if (!candidateResult.error) {
      result = candidateResult
      usedBaseUrl = baseUrl
      break
    }

    result = candidateResult

    if (!isNetworkFallbackError(candidateResult.error)) {
      break
    }
  }

  if (result?.error && result.error.status === 401 && !args.url?.includes('/auth/refresh')) {
    const state = api.getState() as RootState
    const token = state.auth?.token || localStorage.getItem('token')

    if (token) {
      try {
        const refreshData = await refreshToken(usedBaseUrl, token)

        if (refreshData?.access_token) {
          const dispatch = api.dispatch as AppDispatch
          dispatch(setCredentials({ token: refreshData.access_token, user: state.auth.user }))
          const retryQuery = baseQueries.find((entry) => entry.baseUrl === usedBaseUrl)?.query
          if (retryQuery) {
            result = await retryQuery(args, api, extraOptions)
          }
        } else {
          const dispatch = api.dispatch as AppDispatch
          dispatch(logout())
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }
      } catch (error) {
        console.error('[API] Token refresh failed:', error)
        const dispatch = api.dispatch as AppDispatch
        dispatch(logout())
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    } else {
      const dispatch = api.dispatch as AppDispatch
      dispatch(logout())
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
  }

  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Team',
    'Case',
    'Round',
    'Memorial',
    'Evidence',
    'Score',
    'Notification',
    'User',
    'Document',
    'Courtroom',
    'Rubric',
    'JudgeAssignment',
    'Bracket',
    'RoleSheet',
    'KnowledgeItem',
    'HearingMinute',
    'SharedEvidence',
  ],
  endpoints: () => ({}),
})
